/* eslint-disable @typescript-eslint/no-explicit-any */

import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { UserAppointmentMessageList } from "@/Components/MessageScheduler";
import { useCalendarStore } from "@/pages/Dashboard/Calendar/store/useCalendarStore";
import { cn, getLocalizedName } from "@/utils";
import { useForm, UseFormReturnType } from "@mantine/form";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useEffect, memo, ChangeEvent } from "react";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import {
  emailValid,
  numberAndCountryCodeToFullPhoneNumber,
} from "../../../Utilities";
import usePreferredLanguageStore from "../../../stores/usePreferredLanguageStore";
import { useAppointmentModalStore } from "../useAppointmentModalStore_";
import { AppointmentNotificationsSection } from "./AppointmentNotificationsSection";
import { MessageModal } from "./UserAppointmentMessages";
import { api } from "@/lib/api-client";
import {
  isAnyLocationExcludedFromOnlinePayments,
  resolveDefaultCreatePayment,
  syncCreatePaymentWithLocationExclusion,
} from "@/lib/onlinePayments";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Divider,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
  Spinner,
  Tab,
  Tabs,
  useDisclosure,
} from "@heroui/react";
import { i18n } from "@lingui/core";

const APPOINTMENT_SCOPED_EVENTS = new Set([
  "appointmentReminder",
  "appointmentFollowUp",
  "thankYouVisiting",
  "newAppointment",
]);

function isAppointmentScopedSetting(s: {
  event?: string | null;
  trigger?: string | null;
}): boolean {
  if (s.event) return APPOINTMENT_SCOPED_EVENTS.has(s.event);
  return !!s.trigger;
}

import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { GlobalCustomerIndicator } from "@/Components/NextBase/Customer/GlobalCustomerIndicator";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { LimeLocalePicker } from "@/Components/NextBase/LimeLocalePicker";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { CustomerSubscriptionsList } from "./CustomerSubscriptions/CustomerSubscriptionList";
import { AppointmentCustomerFiles } from "./AppointmentCustomerFiles";
import { shouldOpenContactPersonSection } from "./contactPersonSection";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { parseDate } from "@internationalized/date";
import { computeRepeatCountFromUntil } from "@/pages/Dashboard/Calendar/components/calendar/recurrence";
import {
  getActiveChannelsForVariant,
  pickAppointmentVariant,
} from "@/utils/notification-variant";
import type { NotificationSettingToggle } from "./NotificationSettingsSwitchList";
import {
  ArrowUpRight,
  Mail,
  MessageSquareShare,
  Pencil,
  Phone,
  Plus,
  SquareStack,
  UserRoundPen,
  UserSearch,
  X,
} from "lucide-react";
import { CustomerModal } from "@/pages/Dashboard/TaxRegister/Invoices/POS/CustomerModal";
import { ManageCustomerModal } from "@/Components/NextBase/Customer/ManageCustomerBasicInfoModal";
import { CustomerNoteLabelPicker } from "@/Components/NextBase/Customer/CustomerNoteLabelPicker";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeInfoTooltip } from "@/Components/NextBase/LimeInfoTooltip";
import { useClientFeature } from "@/hooks/useClientFeature";
import { PrescheduledSelectModal } from "./PrescheduledSelectModal";
import { CopyButton } from "@/Components/NextBase/CopyButton";
import { TextWithLinks } from "@/Components/TextWithLinks";
import { useDebouncedCallback, useDebouncedValue } from "@mantine/hooks";
import { useNavigate } from "react-router";
import {
  LimeSelectInput,
  LimeSelectInputProps,
  LimeSelectPhoneInputProps,
  LimeSelectTextInputProps,
} from "@/Components/NextBase/LimeSelectInput";
import type { FeatureId } from "@/server-types";
import { GetCustomers, Language } from "@/server-types";

export type FormValues = {
  customerId: number | null;
  name: string;
  lastName: string;
  email: string;
  countryCode: string;
  gsm: string;
  price: number;
  language: Language;
  notes: string;
  // null = not loaded/touched (server leaves the note as-is); "" = explicit clear. Don't coalesce null→"".
  comment: string | null;
  selectedTagIds: number[];
  selectedDefaultIds: string[];
  isRepeating: boolean;
  repeatType: "week" | "day" | "month";
  repeatInterval: number;
  repeatDuration: number;
  repeatEndType: "date" | "count";
  repeatUntilDate: string | null;
  payOnlyFirstAppointment: boolean;
  sendNotifications: boolean;
  createPayment: boolean;
  overrideCreatePaymentAmount: number | null;
  createPaymentLocale: string;
  createPaymentChannel: "sms" | "email" | "both";
  createPaymentTimeoutMinutes: number;
  extraDetails: any[];
  transferFilesToNewCustomer: boolean;
  customerSubscriptionId: number | null;
  gsmPlaceholder: string;
  emailPlaceholder: string;
  emailVisibility: "full" | "hidden" | "masked";
  gsmVisibility: "full" | "hidden" | "masked";
  additionalPrescheduledAppointmentIds: number[];
  discountCode: number | null;
  contactPersonMode: "none" | "existing" | "new";
  contactCustomerId: number | null;
  contactName: string;
  contactLastName: string;
  contactEmail: string;
  contactCountryCode: string;
  contactGsm: string;
};

const NOTES_TRUNCATE_LENGTH = 50;
const CONTACT_CUSTOMER_FEATURE_ID: FeatureId = "contactCustomer";

const Form = ({
  customerData,
  handleSubmitAppointmentForm,
  isNewAppointment,
  appointmentLocationId,
  selectedServices,
  notificationSchedule,
  notificationLog,
  disabledNotificationSettingIds,
  dayObject,
  fetchAppointmentData,
  isExistingUserAppointmentSelected,
  discountCode,
}: {
  customerData: any;
  handleSubmitAppointmentForm: (values: any) => void;
  isNewAppointment: boolean;
  appointmentLocationId: number | null;
  selectedServices: any[];
  notificationSchedule: any[];
  notificationLog: any[];
  disabledNotificationSettingIds: number[];
  dayObject: any;
  fetchAppointmentData: (appointmentId: number) => void;
  isExistingUserAppointmentSelected?: boolean;
  discountCode: number | null;
}) => {
  const [canSubmitForm, setCanSubmitForm] = React.useState(true);
  const [isSelectingContactCustomer, setIsSelectingContactCustomer] =
    React.useState(false);
  const [isContactPersonSectionOpen, setIsContactPersonSectionOpen] =
    React.useState(false);
  const [isContactPersonSectionDismissed, setIsContactPersonSectionDismissed] =
    React.useState(false);
  const [contactPersonSelectionError, setContactPersonSelectionError] =
    React.useState<string | null>(null);
  const [selectedAutocompleteContact, setSelectedAutocompleteContact] =
    React.useState<GetCustomers["response"]["customers"][number] | null>(null);

  const { isFeatureEnabled } = useClientFeature();
  const isContactCustomerFeatureEnabled = isFeatureEnabled(
    CONTACT_CUSTOMER_FEATURE_ID,
  );

  const navigate = useNavigate();

  const {
    isOpen: isCreateAppointmentWithoutCustomerModalOpen,
    onOpen: onOpenCreateAppointmentWithoutCustomerModal,
    onClose: onCloseCreateAppointmentWithoutCustomerModal,
  } = useDisclosure();

  const { locations, selectedLocationId, calendarData } = useCalendarStore(
    (state) => ({
      locations: state.locations,
      selectedLocationId: state.selectedLocationId,
      calendarData: state.calendarData,
    }),
  );

  // Notification variants are scoped per location (allLocations/locations).
  // Resolve them against the appointment's own location, not the calendar's
  // currently-selected one — these diverge for pre-scheduled/online bookings
  // and whenever the calendar filter is unset, which would otherwise collapse
  // the location axis and list toggles for locations where the notification
  // is disabled.
  const notificationLocationId =
    appointmentLocationId ?? selectedLocationId ?? null;

  const isAnyLocationInExcludedCountriesForOnlinePayments =
    isAnyLocationExcludedFromOnlinePayments(locations);

  const { permissions } = useUserPermissionsStore();
  const canViewCustomerNotesFiles = !!permissions.view_customer_notes_files;
  const canViewOnlinePayments = !!permissions.view_online_payments_page;

  const isPaymentsFeatureEnabled = isFeatureEnabled("payments");
  const isSmsFeatureEnabled = isFeatureEnabled("sms");
  const defaultPaymentChannel: FormValues["createPaymentChannel"] =
    isSmsFeatureEnabled ? "sms" : "email";
  const { data: stripeAccountData } = api.stripe.useGetStripeAccount(
    isPaymentsFeatureEnabled && isNewAppointment && canViewOnlinePayments,
  );
  const canCreateOnlinePayment =
    stripeAccountData?.hasFinishedOnboarding === true &&
    !isAnyLocationInExcludedCountriesForOnlinePayments;

  const {
    data: notificationSettings,
    isFetching: isFetchingNotificationSettings,
  } = api.notification.useGetNotificationSettings();

  const { locale } = i18n;

  const {
    isOpen: isCustomerModalOpen,
    onOpen: onOpenCustomerModal,
    onClose: onCloseCustomerModal,
  } = useDisclosure();

  const {
    isOpen: isManageCustomerModalOpen,
    onOpen: onOpenManageCustomerModal,
    onClose: onCloseManageCustomerModal,
  } = useDisclosure();

  const {
    isOpen: isPrescheduledSelectModalOpen,
    onOpen: onOpenPrescheduledSelectModal,
    onClose: onClosePrescheduledSelectModal,
  } = useDisclosure();

  const {
    appointmentData,
    setCurrentAppointmentGsm,
    setCurrentAppointmentEmail,
    setCanDismissDrawer,
    isCustomOnlinePaymentAmount,
    setIsCustomOnlinePaymentAmount,
    setSelectedServices,
    notificationSettingToggles: notificationSettingTogglesState,
    setNotificationSettingToggles,
    setNotificationSettingEnabled,
    setHiddenDisabledNotificationSettingIds,
  } = useAppointmentModalStore((state) => ({
    appointmentData: state.appointmentData,
    setCurrentAppointmentGsm: state.setCurrentAppointmentGsm,
    setCurrentAppointmentEmail: state.setCurrentAppointmentEmail,
    setCanDismissDrawer: state.setCanDismissDrawer,
    isCustomOnlinePaymentAmount: state.isCustomOnlinePaymentAmount,
    setIsCustomOnlinePaymentAmount: state.setIsCustomOnlinePaymentAmount,
    setSelectedServices: state.setSelectedServices,
    notificationSettingToggles: state.notificationSettings,
    setNotificationSettingToggles: state.setNotificationSettings,
    setNotificationSettingEnabled: state.setNotificationSettingEnabled,
    setHiddenDisabledNotificationSettingIds:
      state.setHiddenDisabledNotificationSettingIds,
  }));

  const notificationSettingToggles: NotificationSettingToggle[] =
    notificationSettingTogglesState;

  const { clientPreferredLanguage } = usePreferredLanguageStore(
    (state) => state.preferredLanguage,
  );

  const defaultLanguage =
    locations.find((location) => location.locationId === notificationLocationId)
      ?.defaultLanguage ?? clientPreferredLanguage;

  const doesAnyServiceRequirePayment = selectedServices.some(
    (service) => service.formPaymentRequired,
  );
  const form = useForm<FormValues>({
    initialValues: {
      customerId: customerData?.customerId ?? null,
      name: customerData?.name ?? "",
      lastName: customerData?.lastName ?? "",
      email: customerData?.email ?? "",
      countryCode: customerData?.countryCode ?? "",
      gsm:
        numberAndCountryCodeToFullPhoneNumber(
          customerData?.gsm,
          customerData?.countryCode,
        ) ?? "",
      language: customerData?.language ?? defaultLanguage,
      price: customerData?.price ?? 0.0,
      // birthday: customerData?.birthday
      //   ? dayjs(customerData?.birthday).toDate()
      //   : null,
      emailVisibility: customerData?.emailVisibility ?? "full",
      gsmVisibility: customerData?.gsmVisibility ?? "full",
      emailPlaceholder: customerData?.email ?? "",
      gsmPlaceholder: customerData?.gsm ?? "",
      notes: customerData?.notes ?? "",
      comment: customerData?.comment ?? null,
      selectedTagIds: [],
      selectedDefaultIds: [],
      isRepeating: false,
      repeatType: "week",
      repeatInterval: 1,
      repeatDuration: 1,
      repeatEndType: "date",
      repeatUntilDate: dayObject
        ? dayjs(dayObject).add(1, "week").format("YYYY-MM-DD")
        : null,
      payOnlyFirstAppointment: false,
      sendNotifications: true,
      createPayment: resolveDefaultCreatePayment({
        locations,
        doesAnyServiceRequirePayment,
        isPaymentDefaultCheckedInDashboard:
          calendarData.isPaymentDefaultCheckedInDashboard,
      }),
      overrideCreatePaymentAmount: null,
      createPaymentLocale: defaultLanguage,
      createPaymentChannel: defaultPaymentChannel,
      createPaymentTimeoutMinutes: calendarData.defaultPaymentTimeoutMinutes,
      extraDetails: customerData?.extraDetails ?? [],
      transferFilesToNewCustomer: false,
      customerSubscriptionId: customerData?.customerSubscriptionId,
      additionalPrescheduledAppointmentIds: [],
      discountCode: discountCode ?? null,
      contactPersonMode:
        isContactCustomerFeatureEnabled && customerData?.contactCustomer
          ? "existing"
          : "none",
      contactCustomerId: isContactCustomerFeatureEnabled
        ? (customerData?.contactCustomer?.customerId ?? null)
        : null,
      contactName: isContactCustomerFeatureEnabled
        ? (customerData?.contactCustomer?.name ?? "")
        : "",
      contactLastName: isContactCustomerFeatureEnabled
        ? (customerData?.contactCustomer?.lastName ?? "")
        : "",
      contactEmail: isContactCustomerFeatureEnabled
        ? (customerData?.contactCustomer?.email ?? "")
        : "",
      contactCountryCode: isContactCustomerFeatureEnabled
        ? (customerData?.contactCustomer?.countryCode ??
          customerData?.countryCode ??
          "")
        : "",
      contactGsm: isContactCustomerFeatureEnabled
        ? (numberAndCountryCodeToFullPhoneNumber(
            customerData?.contactCustomer?.gsm,
            customerData?.contactCustomer?.countryCode,
          ) ?? "")
        : "",
    },

    validate: {
      gsm: (value, values) => {
        if (values.customerId != null) return null;
        if (!value) return null;

        const parsedGsm = parsePhoneNumber(value);
        if (!parsedGsm) {
          return t`Telefonska številka ni veljavna`;
        }

        if (!parsedGsm.isValid()) {
          return t`Telefonska številka ni veljavna`;
        }

        return null;
      },
      contactName: (value, values) => {
        if (values.contactPersonMode !== "new") return null;
        if (!value?.trim()) return t`Ime je obvezno`;
        return null;
      },
      contactLastName: (value, values) => {
        if (values.contactPersonMode !== "new") return null;
        if (!value?.trim()) return t`Priimek je obvezen`;
        return null;
      },
      contactEmail: (value, values) => {
        if (values.contactPersonMode !== "new") return null;
        if (!value) return null;
        return emailValid(value) ? null : t`E-pošta ni veljavna`;
      },
      contactGsm: (value, values) => {
        if (values.contactPersonMode !== "new") return null;
        if (!value) return null;

        const parsedGsm = parsePhoneNumber(value);
        if (!parsedGsm || !parsedGsm.isValid()) {
          return t`Telefonska številka ni veljavna`;
        }

        return null;
      },
    },
  });

  const { data: searchedCustomerData, isFetching: isFetchingSearchedCustomer } =
    api.customer.useGetCustomer({
      customerId: form.values.customerId ?? undefined,
      enabled: form.values.customerId != null,
    });

  const [debouncedDuplicateEmail] = useDebouncedValue(
    form.values.customerId == null ? (form.values.email ?? "").trim() : "",
    400,
  );
  const [debouncedDuplicateGsm] = useDebouncedValue(
    form.values.customerId == null ? (form.values.gsm ?? "") : "",
    400,
  );

  const shouldCheckDuplicateEmail =
    form.values.customerId == null &&
    debouncedDuplicateEmail.length > 3 &&
    emailValid(debouncedDuplicateEmail);
  const shouldCheckDuplicateGsm =
    form.values.customerId == null &&
    debouncedDuplicateGsm.length > 3 &&
    isValidPhoneNumber(debouncedDuplicateGsm);

  const { data: duplicateCustomerByEmailData } =
    api.customer.useGetCheckIfCustomerExists(
      {
        email: debouncedDuplicateEmail ?? undefined,
      },
      shouldCheckDuplicateEmail,
    );

  const { data: duplicateCustomerByGsmData } =
    api.customer.useGetCheckIfCustomerExists(
      {
        gsm: debouncedDuplicateGsm ?? undefined,
      },
      shouldCheckDuplicateGsm,
    );
  const {
    data: contactSuggestionsData,
    isFetching: isFetchingContactSuggestions,
  } = api.customer.useGetCustomerContactCustomers({
    customerId: form.values.customerId ?? undefined,
    enabled: isContactCustomerFeatureEnabled && form.values.customerId != null,
  });

  useEffect(() => {
    form.setValues({
      discountCode: discountCode ?? null,
    });
  }, [discountCode]);

  useEffect(() => {
    const nextCreatePayment = syncCreatePaymentWithLocationExclusion(
      form.values.createPayment,
      locations,
    );
    if (nextCreatePayment !== form.values.createPayment) {
      form.setFieldValue("createPayment", nextCreatePayment);
    }
  }, [locations, form.values.createPayment]);

  useEffect(() => {
    if (!form.values.customerId) {
      return;
    }

    // Ensure fetched data matches the selected customer
    if (searchedCustomerData?.customerId !== form.values.customerId) {
      return;
    }

    const { gsm, email } = (() => {
      if (!searchedCustomerData) {
        return { gsm: undefined, email: undefined };
      }

      let gsm: string | undefined;
      let email: string | undefined;

      if (searchedCustomerData.gsmVisibility === "full") {
        gsm = searchedCustomerData.gsm ?? undefined;
      } else {
        gsm = undefined;
      }

      if (searchedCustomerData.emailVisibility === "full") {
        email = searchedCustomerData.email ?? undefined;
      } else {
        email = undefined;
      }

      return { gsm, email };
    })();

    setCurrentAppointmentGsm(gsm);
    setCurrentAppointmentEmail(email);

    form.setFieldValue("name", searchedCustomerData.name ?? "");
    form.setFieldValue("lastName", searchedCustomerData.lastName ?? "");
    form.setFieldValue(
      "email",
      searchedCustomerData.emailVisibility === "full"
        ? (searchedCustomerData.email ?? "")
        : "",
    );
    form.setFieldValue(
      "gsm",
      searchedCustomerData.gsmVisibility === "full"
        ? (numberAndCountryCodeToFullPhoneNumber(
            searchedCustomerData.gsm,
            searchedCustomerData.countryCode,
          ) ?? "")
        : "",
    );
    form.setFieldValue("countryCode", searchedCustomerData.countryCode ?? "");
    form.setFieldValue(
      "language",
      searchedCustomerData.language ?? defaultLanguage,
    );
  }, [form.values.customerId, searchedCustomerData]);

  const {
    data: calculatedPaymentPriceData,
    isPending: isPaymentPricePending,
    processedErrorMessage: paymentPriceErrorMessage,
  } = api.appointments.useGetCalculatePaymentPrice(
    {
      appointmentParts: selectedServices.map((service) => ({
        ...service,
        serviceId: Number(service.serviceId),
        priceCents: Math.round(service.price * 100),
        discountAmount: service.discountAmount
          ? service.discountAmount *
            (service.discountType == "amount" ? 100 : 1)
          : 0,
      })),
      repeatData: form.getValues().isRepeating
        ? {
            type: form.getValues().repeatType as "week" | "day" | "month",
            interval: form.getValues().repeatInterval || 1,
            count: form.getValues().repeatDuration || 1,
          }
        : undefined,
      paymentDetails: {
        onlyFirstAppointment: form.getValues().payOnlyFirstAppointment,
      },
      startTime: dayObject?.format("YYYY-MM-DDTHH:mm"),
      locationId: selectedLocationId,
    },
    form.getValues().createPayment && canCreateOnlinePayment,
  );

  const {
    data: notificationChannels,
    isPending: isFetchingNotificationChannels,
  } = api.notification.useGetNotificationChannels();

  // Keep repeatDuration (the occurrence count consumed on submit and by the
  // payment-price estimate) in sync while the user picks an end date instead
  // of a count.
  useEffect(() => {
    if (
      !form.values.isRepeating ||
      form.values.repeatEndType !== "date" ||
      !form.values.repeatUntilDate
    ) {
      return;
    }
    const count = computeRepeatCountFromUntil({
      startDate: dayObject,
      untilDate: form.values.repeatUntilDate,
      interval: form.values.repeatInterval,
      type: form.values.repeatType,
    });
    if (count !== form.values.repeatDuration) {
      form.setFieldValue("repeatDuration", count);
    }
  }, [
    form.values.isRepeating,
    form.values.repeatEndType,
    form.values.repeatUntilDate,
    form.values.repeatInterval,
    form.values.repeatType,
    dayObject,
  ]);

  useEffect(() => {
    if (customerData == null) {
      form.setValues({
        customerId: null,
        name: "",
        lastName: "",
        email: "",
        countryCode: "",
        gsm: "",
        price: 0.0,
        // birthday: null,
        notes: "",
        comment: null,
        selectedTagIds: [],
        selectedDefaultIds: [],
        isRepeating: false,
        repeatType: "week",
        repeatInterval: 1,
        repeatDuration: 1,
        repeatEndType: "date",
        repeatUntilDate: dayObject
          ? dayjs(dayObject).add(1, "week").format("YYYY-MM-DD")
          : null,
        sendNotifications: true,
        createPayment: resolveDefaultCreatePayment({
          locations,
          doesAnyServiceRequirePayment,
          isPaymentDefaultCheckedInDashboard:
            calendarData.isPaymentDefaultCheckedInDashboard,
        }),
        createPaymentTimeoutMinutes: calendarData.defaultPaymentTimeoutMinutes,
        createPaymentLocale: defaultLanguage,
        createPaymentChannel: defaultPaymentChannel,
        extraDetails: [],
        transferFilesToNewCustomer: false,
        customerSubscriptionId: null,
        overrideCreatePaymentAmount: null,
        contactPersonMode: "none",
        contactCustomerId: null,
        contactName: "",
        contactLastName: "",
        contactEmail: "",
        contactCountryCode: "",
        contactGsm: "",
      });
      return;
    }

    if (customerData?.userAppointmentId) {
      form.setValues({
        customerId: customerData?.customerId,
        name: customerData?.name,
        lastName: customerData?.lastName,
        email:
          customerData?.emailVisibility === "full" ? customerData?.email : "",
        countryCode: customerData?.countryCode,
        gsm: customerData?.gsmVisibility === "full" ? customerData?.gsm : "",
        price: customerData?.price ?? 0.0,
        // birthday: customerData?.birthday
        //   ? dayjs(customerData?.birthday).toDate()
        //   : null,
        notes: customerData?.notes,
        comment: customerData?.comment ?? null,
        emailVisibility: customerData?.emailVisibility ?? "full",
        gsmVisibility: customerData?.gsmVisibility ?? "full",
        emailPlaceholder: customerData?.email,
        gsmPlaceholder: customerData?.gsm,
        isRepeating: false,
        repeatType: "week",
        repeatInterval: 1,
        repeatDuration: 1,
        repeatEndType: "date",
        repeatUntilDate: dayObject
          ? dayjs(dayObject).add(1, "week").format("YYYY-MM-DD")
          : null,
        extraDetails: customerData?.extraDetails ?? [],
        transferFilesToNewCustomer: false,
        customerSubscriptionId: customerData?.customerSubscriptionId,
        overrideCreatePaymentAmount: null,
        contactPersonMode:
          isContactCustomerFeatureEnabled && customerData?.contactCustomer
            ? "existing"
            : "none",
        contactCustomerId: isContactCustomerFeatureEnabled
          ? (customerData?.contactCustomer?.customerId ?? null)
          : null,
        contactName: isContactCustomerFeatureEnabled
          ? (customerData?.contactCustomer?.name ?? "")
          : "",
        contactLastName: isContactCustomerFeatureEnabled
          ? (customerData?.contactCustomer?.lastName ?? "")
          : "",
        contactEmail: isContactCustomerFeatureEnabled
          ? (customerData?.contactCustomer?.email ?? "")
          : "",
        contactCountryCode: isContactCustomerFeatureEnabled
          ? (customerData?.contactCustomer?.countryCode ??
            customerData?.countryCode ??
            "")
          : "",
        contactGsm: isContactCustomerFeatureEnabled
          ? (numberAndCountryCodeToFullPhoneNumber(
              customerData?.contactCustomer?.gsm,
              customerData?.contactCustomer?.countryCode,
            ) ?? "")
          : "",
        selectedTagIds:
          customerData?.tags?.map((tag: { id: number }) => tag.id) || [],
        selectedDefaultIds: [],
      });
    }
  }, [customerData?.userAppointmentId, isContactCustomerFeatureEnabled]);

  // Toggles the user already flipped survive a recompute (service or time
  // change), but never carry over to a different attendee.
  const notificationToggleContextRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (notificationSettings && appointmentData) {
      const isNew =
        isNewAppointment || (appointmentData?.isPreScheduled && !customerData);

      const toggleContextKey = `${customerData?.userAppointmentId ?? "none"}`;
      const keepPreviousToggles =
        notificationToggleContextRef.current === toggleContextKey;
      notificationToggleContextRef.current = toggleContextKey;

      const serviceIds = selectedServices.map((service) => service.serviceId);

      const mergedSettings = (notificationSettings.settings ?? [])
        .filter((s) => {
          if (!s.isEnabled) return false;
          if (!isAppointmentScopedSetting(s)) return false;
          if (s.trigger === "onCancellation" || s.trigger === "onReschedule") {
            return false;
          }

          const variants = s.variants ?? [];
          const locationId = notificationLocationId;
          const serviceIdsToCheck: (number | null)[] =
            serviceIds.length > 0 ? serviceIds : [null];
          const isCorrectService =
            variants.length > 0 &&
            serviceIdsToCheck.some((serviceId) => {
              const normalizedServiceId =
                serviceId == null ? null : Number(serviceId);
              const picked = pickAppointmentVariant(variants, {
                serviceId: normalizedServiceId,
                locationId,
              });
              return getActiveChannelsForVariant(picked).length > 0;
            });

          const scheduledTime = (() => {
            if (
              s.trigger === "cronBeforeAppointment" ||
              s.event === "appointmentReminder"
            ) {
              return dayjs(appointmentData.startTime)
                .subtract(s.timeOffset, "minutes")
                .format("YYYY-MM-DDTHH:mm");
            } else if (
              s.trigger === "cronAfterAppointment" ||
              s.event === "appointmentFollowUp" ||
              s.event === "thankYouVisiting"
            ) {
              return dayjs(appointmentData.startTime)
                .add(appointmentData.duration, "minutes")
                .add(s.timeOffset, "minutes")
                .format("YYYY-MM-DDTHH:mm");
            }

            return null;
          })();

          const isCorrectTime =
            scheduledTime === null
              ? true
              : dayjs(scheduledTime).isAfter(dayjs());

          if (isNew) {
            return isCorrectService && isCorrectTime;
          } else {
            const isScheduled =
              ["cronBeforeAppointment", "cronAfterAppointment"].includes(
                s.trigger ?? "",
              ) ||
              (!!s.event &&
                [
                  "appointmentReminder",
                  "appointmentFollowUp",
                  "thankYouVisiting",
                ].includes(s.event));
            if (!isScheduled) {
              return false;
            }

            return (
              isCorrectService &&
              (isCorrectTime ||
                notificationSchedule.some(
                  (ns) =>
                    ns.notificationSettingId === s.id &&
                    ns.status === "scheduled",
                ))
            );
          }
        })
        .toSorted((a, b) => {
          // onConfirmation triggers should be first
          if (a.trigger === "onConfirmation") {
            return -1;
          }
          if (b.trigger === "onConfirmation") {
            return 1;
          }

          // cronBeforeAppointment triggers sorted by time_offset (descending)
          if (
            a.trigger === "cronBeforeAppointment" &&
            b.trigger === "cronBeforeAppointment"
          ) {
            return (b.timeOffset || 0) - (a.timeOffset || 0);
          }
          if (a.trigger === "cronBeforeAppointment") {
            return -1;
          }
          if (b.trigger === "cronBeforeAppointment") {
            return 1;
          }

          // cronAfterAppointment triggers sorted by time_offset (ascending)
          if (
            a.trigger === "cronAfterAppointment" &&
            b.trigger === "cronAfterAppointment"
          ) {
            return (a.timeOffset || 0) - (b.timeOffset || 0);
          }
          if (a.trigger === "cronAfterAppointment") {
            return -1;
          }
          if (b.trigger === "cronAfterAppointment") {
            return 1;
          }

          // Any other triggers come last
          return 0;
        })
        .map((setting) => {
          const currentToggles: NotificationSettingToggle[] =
            useAppointmentModalStore.getState().notificationSettings;
          const previousToggle = keepPreviousToggles
            ? currentToggles.find((s) => s.id === setting.id)
            : undefined;

          if (isNew) {
            return {
              id: setting.id,
              enabled: previousToggle?.enabled ?? true,
            };
          }

          // On an existing appointment a notification defaults to on unless it
          // was switched off for this appointment: a reminder that already
          // fired has no schedule row left, and keying off that made every
          // reschedule silently skip it (DEV-9564).
          return {
            id: setting.id,
            enabled:
              previousToggle?.enabled ??
              !disabledNotificationSettingIds.includes(setting.id),
          };
        });

      setNotificationSettingToggles(mergedSettings);

      const toggleableIds = new Set(mergedSettings.map((s) => s.id));
      setHiddenDisabledNotificationSettingIds(
        Array.from(
          new Set(
            disabledNotificationSettingIds.filter(
              (settingId) => !toggleableIds.has(settingId),
            ),
          ),
        ),
      );
    }
  }, [
    notificationSettings,
    selectedServices,
    notificationLocationId,
    appointmentData?.startTime,
    appointmentData?.duration,
    customerData,
  ]);

  const [combinedCustomerSearch, setCombinedCustomerSearch] =
    React.useState("");
  const [currentSearchField, setCurrentSearchField] = React.useState<
    "name" | "lastName" | "email" | "gsm" | undefined
  >(undefined);
  const [combinedContactCustomerSearch, setCombinedContactCustomerSearch] =
    React.useState("");
  const [currentContactSearchField, setCurrentContactSearchField] =
    React.useState<"name" | "lastName" | "email" | "gsm" | undefined>(
      undefined,
    );
  const handleSearchCustomers = useDebouncedCallback(async () => {
    const currentValues = form.getValues();

    const parsedGsm = currentValues.gsm
      ? parsePhoneNumber(currentValues.gsm)
      : "";

    setCombinedCustomerSearch(
      [
        currentValues.name,
        currentValues.lastName,
        currentValues.email,
        parsedGsm ? parsedGsm.nationalNumber : "",
      ]
        .filter(Boolean)
        .join(" "),
    );
  }, 300);
  const handleSearchContactCustomers = useDebouncedCallback(async () => {
    const currentValues = form.getValues();

    const parsedGsm = currentValues.contactGsm
      ? parsePhoneNumber(currentValues.contactGsm)
      : "";

    setCombinedContactCustomerSearch(
      [
        currentValues.contactName,
        currentValues.contactLastName,
        currentValues.contactEmail,
        parsedGsm ? parsedGsm.nationalNumber : "",
      ]
        .filter(Boolean)
        .join(" "),
    );
  }, 300);

  const { data: customersData, isFetching: isFetchingCustomers } =
    api.customer.useGetCustomers({
      searchQuery: combinedCustomerSearch,
      searchField: currentSearchField,
      enabled: combinedCustomerSearch.length > 2,
      sortByLocationId: selectedLocationId ?? undefined,
    });
  const { data: contactCustomersData, isFetching: isFetchingContactCustomers } =
    api.customer.useGetCustomers({
      searchQuery: combinedContactCustomerSearch,
      searchField: currentContactSearchField,
      enabled: combinedContactCustomerSearch.length > 2,
      sortByLocationId: selectedLocationId ?? undefined,
    });

  const clearContactPersonFields = React.useCallback(() => {
    setIsSelectingContactCustomer(false);
    setSelectedAutocompleteContact(null);
    form.setFieldValue("contactPersonMode", "none");
    form.setFieldValue("contactCustomerId", null);
    form.setFieldValue("contactName", "");
    form.setFieldValue("contactLastName", "");
    form.setFieldValue("contactEmail", "");
    form.setFieldValue("contactGsm", "");
    form.setFieldValue(
      "contactCountryCode",
      form.getValues().countryCode || "",
    );
    form.clearFieldError("contactName");
    form.clearFieldError("contactLastName");
    form.clearFieldError("contactEmail");
    form.clearFieldError("contactGsm");
    setCombinedContactCustomerSearch("");
    setCurrentContactSearchField(undefined);
  }, [form]);

  const selectExistingContactCustomer = React.useCallback(
    (customer: GetCustomers["response"]["customers"][number]) => {
      setIsSelectingContactCustomer(false);
      setSelectedAutocompleteContact(customer);
      form.setFieldValue("contactPersonMode", "existing");
      form.setFieldValue("contactCustomerId", customer.customerId);
      form.setFieldValue("contactName", "");
      form.setFieldValue("contactLastName", "");
      form.setFieldValue("contactEmail", "");
      form.setFieldValue("contactGsm", "");
      form.setFieldValue("contactCountryCode", "");
      form.clearFieldError("contactName");
      form.clearFieldError("contactLastName");
      form.clearFieldError("contactEmail");
      form.clearFieldError("contactGsm");
      setCombinedContactCustomerSearch("");
      setCurrentContactSearchField(undefined);
    },
    [form],
  );

  const contactSuggestions = React.useMemo(() => {
    const suggestions = contactSuggestionsData?.contactCustomers ?? [];
    const currentContact = customerData?.contactCustomer;

    if (!currentContact) {
      return suggestions;
    }

    if (
      suggestions.some(
        (contact) => contact.customerId === currentContact.customerId,
      )
    ) {
      return suggestions;
    }

    return [
      { ...currentContact, relationCount: 0, lastRelatedAt: null },
      ...suggestions,
    ];
  }, [contactSuggestionsData, customerData?.contactCustomer]);

  const selectedExistingContact = React.useMemo(() => {
    if (
      form.values.contactPersonMode !== "existing" ||
      form.values.contactCustomerId == null
    ) {
      return null;
    }

    const suggestedContact =
      contactSuggestions.find(
        (contact) => contact.customerId === form.values.contactCustomerId,
      ) ?? null;

    if (suggestedContact) {
      return suggestedContact;
    }

    if (
      selectedAutocompleteContact?.customerId === form.values.contactCustomerId
    ) {
      return {
        ...selectedAutocompleteContact,
        relationCount: 0,
        lastRelatedAt: null,
      };
    }

    return null;
  }, [
    contactSuggestions,
    form.values.contactCustomerId,
    form.values.contactPersonMode,
    selectedAutocompleteContact,
  ]);

  const isNewContactSelected = form.values.contactPersonMode === "new";
  const duplicateCustomerWarnings = React.useMemo(() => {
    const warningsByCustomerId = new Map<
      number,
      {
        customerId: number;
        matchedFields: Array<"email" | "gsm">;
      }
    >();

    const addWarning = ({
      exists,
      customerId,
      field,
    }: {
      exists?: boolean;
      customerId?: number | null;
      field: "email" | "gsm";
    }) => {
      if (!exists || customerId == null) {
        return;
      }

      const existingWarning = warningsByCustomerId.get(customerId);
      if (existingWarning) {
        if (!existingWarning.matchedFields.includes(field)) {
          existingWarning.matchedFields.push(field);
        }
        return;
      }

      warningsByCustomerId.set(customerId, {
        customerId,
        matchedFields: [field],
      });
    };

    addWarning({
      exists: duplicateCustomerByEmailData?.exists,
      customerId: duplicateCustomerByEmailData?.customerId,
      field: "email",
    });
    addWarning({
      exists: duplicateCustomerByGsmData?.exists,
      customerId: duplicateCustomerByGsmData?.customerId,
      field: "gsm",
    });

    return Array.from(warningsByCustomerId.values());
  }, [duplicateCustomerByEmailData, duplicateCustomerByGsmData]);

  const isPreScheduled = appointmentData?.isPreScheduled;
  const hasHistoricalContactSuggestions =
    (contactSuggestionsData?.contactCustomers?.length ?? 0) > 0;
  const isAddingCustomerToPrescheduledAppointment =
    isPreScheduled && !isExistingUserAppointmentSelected;
  const shouldAutoOpenContactSectionFromSuggestions =
    !isContactPersonSectionDismissed &&
    hasHistoricalContactSuggestions &&
    (isNewAppointment || isAddingCustomerToPrescheduledAppointment);
  const shouldConfirmCreateCustomerWithoutData =
    (isNewAppointment || isAddingCustomerToPrescheduledAppointment) &&
    !form.values.name &&
    !form.values.customerId;

  const cancelContactPersonSelection = React.useCallback(() => {
    clearContactPersonFields();
    setIsContactPersonSectionOpen(false);
    setIsContactPersonSectionDismissed(true);
    setContactPersonSelectionError(null);
  }, [clearContactPersonFields]);

  const openContactCustomerCard = React.useCallback(
    (customerId: number) => {
      useCalendarStore.getState().closeModals();
      navigate(`/dashboard/customers/${customerId}`);
    },
    [navigate],
  );

  const selectDuplicateCustomer = React.useCallback(
    (customerId: number) => {
      form.setFieldValue("comment", null);
      form.setFieldValue("customerId", customerId);
      form.setFieldValue("customerSubscriptionId", null);
      setCombinedCustomerSearch("");
      clearContactPersonFields();
    },
    [clearContactPersonFields, form],
  );

  function getDuplicateCustomerAlertMessage(
    matchedFields: Array<"email" | "gsm">,
  ) {
    if (matchedFields.length === 2) {
      return t`Stranka s tem e-poštnim naslovom in telefonsko številko že obstaja. Želite izbrati obstoječo stranko?`;
    }

    if (matchedFields.includes("email")) {
      return t`Stranka s tem e-poštnim naslovom že obstaja. Želite izbrati obstoječo stranko?`;
    }

    return t`Stranka s to telefonsko številko že obstaja. Želite izbrati obstoječo stranko?`;
  }

  const submitAppointment = (values: FormValues) =>
    handleSubmitAppointmentForm({
      ...values,
      createPayment: values.createPayment && canCreateOnlinePayment,
    });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmitForm) return;
    setCanSubmitForm(false);

    if (shouldConfirmCreateCustomerWithoutData) {
      setCanDismissDrawer(false);

      onOpenCreateAppointmentWithoutCustomerModal();
    } else {
      if (
        isContactCustomerFeatureEnabled &&
        isContactPersonSectionOpen &&
        form.getValues().contactPersonMode === "none"
      ) {
        setContactPersonSelectionError(
          t`Izberi kontaktno osebo ali prekliči izbor.`,
        );
        setCanSubmitForm(true);
        return;
      }

      if (
        isContactCustomerFeatureEnabled &&
        form.getValues().contactPersonMode === "existing" &&
        form.getValues().customerId != null &&
        form.getValues().customerId === form.getValues().contactCustomerId
      ) {
        setContactPersonSelectionError(
          t`Kontaktna oseba ne more biti ista kot stranka.`,
        );
        setCanSubmitForm(true);
        return;
      }

      const validation = form.validate();
      if (validation.hasErrors) {
        setCanSubmitForm(true);
        return;
      }

      setCanDismissDrawer(true);
      submitAppointment(
        isContactCustomerFeatureEnabled
          ? form.values
          : {
              ...form.values,
              contactPersonMode: "none",
              contactCustomerId: null,
              contactName: "",
              contactLastName: "",
              contactEmail: "",
              contactCountryCode: "",
              contactGsm: "",
            },
      );
    }
    setCanSubmitForm(true);
  };

  const willChangeCustomer =
    !isPreScheduled &&
    !isNewAppointment &&
    form.getValues().customerId != appointmentData?.customers[0]?.customerId;

  const areNotificationSettingsLoading =
    isFetchingNotificationSettings || isFetchingNotificationChannels;

  useEffect(() => {
    if (!isContactCustomerFeatureEnabled) {
      if (isContactPersonSectionOpen) {
        setIsContactPersonSectionOpen(false);
      }

      if (isContactPersonSectionDismissed) {
        setIsContactPersonSectionDismissed(false);
      }

      if (isSelectingContactCustomer) {
        setIsSelectingContactCustomer(false);
      }

      if (contactPersonSelectionError != null) {
        setContactPersonSelectionError(null);
      }

      if (
        form.values.contactPersonMode !== "none" ||
        form.values.contactCustomerId != null ||
        form.values.contactName !== "" ||
        form.values.contactLastName !== "" ||
        form.values.contactEmail !== "" ||
        form.values.contactGsm !== "" ||
        form.values.contactCountryCode !== (form.getValues().countryCode || "")
      ) {
        clearContactPersonFields();
      }

      return;
    }

    if (
      shouldOpenContactPersonSection({
        isOpen: isContactPersonSectionOpen,
        shouldAutoOpenFromSuggestions:
          shouldAutoOpenContactSectionFromSuggestions,
        contactPersonMode: form.values.contactPersonMode,
      })
    ) {
      setIsContactPersonSectionOpen(true);
    }
  }, [
    contactPersonSelectionError,
    isContactCustomerFeatureEnabled,
    isContactPersonSectionDismissed,
    isContactPersonSectionOpen,
    isSelectingContactCustomer,
    clearContactPersonFields,
    shouldAutoOpenContactSectionFromSuggestions,
    form.values.contactCountryCode,
    form.values.contactCustomerId,
    form.values.contactEmail,
    form.values.contactGsm,
    form.values.contactLastName,
    form.values.contactName,
    form.values.contactPersonMode,
  ]);

  useEffect(() => {
    setIsContactPersonSectionDismissed(false);
    setContactPersonSelectionError(null);
  }, [form.values.customerId]);

  useEffect(() => {
    if (form.values.contactPersonMode !== "none") {
      setContactPersonSelectionError(null);
    }
  }, [form.values.contactPersonMode]);

  return (
    <>
      <div>
        <div>
          <form
            id={"appointment-form"}
            data-identifier={"appointment-form"}
            onSubmit={handleSubmit}
          >
            <Divider className="mb-2" />

            <p className="mb-2 text-sm leading-5 font-medium">
              <Trans>Stranka</Trans>{" "}
            </p>

            <div
              className={cn(
                "mb-2 flex flex-col gap-3",
                isContactCustomerFeatureEnabled
                  ? "border-default-200 rounded-xl border p-3 md:p-4"
                  : "",
              )}
            >
              <div className="flex flex-col gap-2">
                {form.values.customerId == null && (
                  <>
                    <div className="relative flex flex-row justify-between gap-2">
                      <FormInput
                        form={form}
                        isFetchingCustomers={isFetchingCustomers}
                        customersData={customersData}
                        onCustomerSelected={() => {
                          clearContactPersonFields();
                        }}
                        selectInputProps={{
                          inputType: "text",
                          inputProps: {
                            ...form.getInputProps("name"),
                            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                              form.setFieldValue("name", e.target.value);
                              setCurrentSearchField("name");
                              handleSearchCustomers();
                            },
                            label: t`Ime`,
                            autoFocus: true,
                            "data-identifier": "name",
                          },
                          wrapperClassName: "static", // span over both inputs (name and lastName)
                        }}
                        setCombinedCustomerSearch={setCombinedCustomerSearch}
                        selectFirstOnEnter={true}
                      />

                      <FormInput
                        form={form}
                        isFetchingCustomers={isFetchingCustomers}
                        customersData={customersData}
                        onCustomerSelected={() => {
                          clearContactPersonFields();
                        }}
                        selectInputProps={{
                          inputType: "text",
                          inputProps: {
                            ...form.getInputProps("lastName"),
                            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                              form.setFieldValue("lastName", e.target.value);
                              setCurrentSearchField("lastName");
                              handleSearchCustomers();
                            },
                            label: t`Priimek`,
                            "data-identifier": "last-name",
                          },
                          wrapperClassName: "static", // span over both inputs (name and lastName)
                        }}
                        setCombinedCustomerSearch={setCombinedCustomerSearch}
                        selectFirstOnEnter={true}
                      />
                    </div>

                    <FormInput
                      form={form}
                      isFetchingCustomers={isFetchingCustomers}
                      customersData={customersData}
                      onCustomerSelected={() => {
                        clearContactPersonFields();
                      }}
                      selectInputProps={{
                        inputType: "text",
                        inputProps: {
                          ...form.getInputProps("email"),
                          onChange: (e: ChangeEvent<HTMLInputElement>) => {
                            form.setFieldValue("email", e.target.value);
                            setCurrentSearchField("email");
                            handleSearchCustomers();
                            setCurrentAppointmentEmail(e.target.value);
                          },
                          label: t`E-pošta`,
                          placeholder: form.getValues().emailPlaceholder,
                          error:
                            form.getValues().email &&
                            !emailValid(form.getValues().email)
                              ? t`E-pošta ni veljavna`
                              : undefined,
                          "data-identifier": "email",
                        },
                      }}
                      setCombinedCustomerSearch={setCombinedCustomerSearch}
                      selectFirstOnEnter={true}
                    />

                    <FormInput
                      form={form}
                      isFetchingCustomers={isFetchingCustomers}
                      customersData={customersData}
                      onCustomerSelected={() => {
                        clearContactPersonFields();
                      }}
                      selectInputProps={{
                        inputType: "phone",
                        inputProps: {
                          ...form.getInputProps("gsm"),
                          onChange: (
                            value: Extract<
                              LimeSelectInputProps,
                              { inputType: "phone" }
                            >["inputProps"]["onChange"],
                          ) => {
                            form.setFieldValue("gsm", value);
                            setCurrentSearchField("gsm");
                            handleSearchCustomers();
                            setCurrentAppointmentGsm(value);
                          },
                          label: t`Telefonska številka`,
                          placeholder: form.getValues().gsmPlaceholder,
                          defaultCountry:
                            locations?.find(
                              (l: any) => l.locationId === selectedLocationId,
                            )?.country || "SI",
                          error:
                            form.getValues().gsm &&
                            !isValidPhoneNumber(form.getValues().gsm)
                              ? t`Telefonska številka ni veljavna`
                              : undefined,
                          "data-identifier": "phone-input",
                          autoComplete: "do-not-autofill",
                          variant: "flat",
                        },
                      }}
                      setCombinedCustomerSearch={setCombinedCustomerSearch}
                      selectFirstOnEnter={true}
                    />

                    {duplicateCustomerWarnings.map((warning) => (
                      <LimeAlert
                        key={warning.customerId}
                        color="warning"
                        title={getDuplicateCustomerAlertMessage(
                          warning.matchedFields,
                        )}
                      >
                        <div className="mt-2 flex justify-end">
                          <Button
                            size="sm"
                            variant="flat"
                            onPress={() =>
                              selectDuplicateCustomer(warning.customerId)
                            }
                          >
                            <Trans>Izberi stranko</Trans>
                          </Button>
                        </div>
                      </LimeAlert>
                    ))}

                    {isNewAppointment && (
                      <LimeLocalePicker
                        {...form.getInputProps("language")}
                        data-identifier="customer-language-picker"
                      />
                    )}
                  </>
                )}

                <div className="flex items-center justify-between">
                  {form.values.customerId != null ? (
                    <div>
                      {customerData != null && (
                        <div>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="flat"
                              size="sm"
                              startContent={<UserRoundPen size={16} />}
                              onPress={onOpenManageCustomerModal}
                            >
                              <Trans>Uredi stranko</Trans>
                            </Button>
                            {!isPreScheduled && (
                              <Button
                                variant="flat"
                                size="sm"
                                startContent={<UserSearch size={16} />}
                                onPress={onOpenCustomerModal}
                              >
                                <Trans>Zamenjaj stranko</Trans>
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {willChangeCustomer && (
                  <div>
                    <p className="text-danger-500 text-xs">
                      <Trans>
                        Stranka bo zamenjana z novo stranko ob posodobitvi
                        termina
                      </Trans>
                    </p>
                    {isFeatureEnabled("customerFiles") &&
                    canViewCustomerNotesFiles ? (
                      <LimeSwitch
                        title={t`Prenesi datoteke termina na novo stranko`}
                        {...form.getInputProps("transferFilesToNewCustomer", {
                          type: "checkbox",
                        })}
                        size="sm"
                      />
                    ) : undefined}
                  </div>
                )}

                {form.values.customerId ? (
                  <div>
                    <Skeleton
                      isLoaded={!isFetchingSearchedCustomer}
                      className="rounded-xl"
                    >
                      <Card
                        className="flex flex-1 flex-col border-1 border-gray-200"
                        shadow="none"
                      >
                        <CardHeader className="flex items-center justify-between">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                              {searchedCustomerData?.name}{" "}
                              {searchedCustomerData?.lastName}
                              {searchedCustomerData?.isGlobalCustomer ? (
                                <GlobalCustomerIndicator size={16} />
                              ) : null}
                            </p>
                            {customerData?.customerId ===
                              form.values.customerId &&
                            customerData?.isFirstAppointment ? (
                              <Chip
                                color="secondary"
                                size="sm"
                                variant="flat"
                                radius="sm"
                              >
                                <Trans>Nova stranka</Trans>
                              </Chip>
                            ) : null}
                            {customerData?.customerId ===
                              form.values.customerId &&
                            customerData?.customerSubscriptionCredit ? (
                              <Chip
                                color="warning"
                                size="sm"
                                variant="flat"
                                radius="sm"
                              >
                                <Trans>Uporabljen dodatni termin</Trans>
                              </Chip>
                            ) : null}
                          </div>

                          {(!appointmentData?.isPreScheduled &&
                            isNewAppointment) ||
                          isAddingCustomerToPrescheduledAppointment ? (
                            <Button
                              variant="flat"
                              size="sm"
                              startContent={<X size={16} />}
                              onPress={() => {
                                form.setFieldValue("customerId", null);
                                form.setFieldValue("name", "");
                                form.setFieldValue("lastName", "");
                                form.setFieldValue("email", "");
                                form.setFieldValue("gsm", "");
                                form.setFieldValue("notes", "");
                                form.setFieldValue("comment", null);
                                form.setFieldValue("emailVisibility", "full");
                                form.setFieldValue("gsmVisibility", "full");

                                form.setFieldValue("emailPlaceholder", "");
                                form.setFieldValue("gsmPlaceholder", "");
                                form.setFieldValue(
                                  "customerSubscriptionId",
                                  null,
                                );
                                clearContactPersonFields();
                              }}
                              className="mt-1"
                            >
                              <Trans>Počisti</Trans>
                            </Button>
                          ) : null}
                        </CardHeader>
                        <CardBody className="flex flex-col gap-1 pt-0">
                          {searchedCustomerData?.email &&
                            searchedCustomerData?.emailVisibility !==
                              "hidden" && (
                              <div className="flex items-center gap-1">
                                <Button
                                  startContent={<Mail size={16} />}
                                  variant="flat"
                                  size="sm"
                                  className="w-fit"
                                  as={Link}
                                  href={`mailto:${searchedCustomerData?.email}`}
                                >
                                  {searchedCustomerData?.email}
                                </Button>

                                <CopyButton
                                  size="sm"
                                  variant="secondary"
                                  text={searchedCustomerData.email}
                                />
                              </div>
                            )}
                          {searchedCustomerData?.gsm &&
                            searchedCustomerData?.gsmVisibility !==
                              "hidden" && (
                              <div className="flex items-center gap-1">
                                <Button
                                  startContent={<Phone size={16} />}
                                  variant="flat"
                                  size="sm"
                                  className="w-fit"
                                  as={Link}
                                  href={`tel:${searchedCustomerData.gsm}`}
                                >
                                  {searchedCustomerData.gsm}
                                </Button>

                                <CopyButton
                                  size="sm"
                                  variant="secondary"
                                  text={searchedCustomerData.gsm}
                                />

                                <Button
                                  isIconOnly
                                  size="sm"
                                  variant="flat"
                                  as={Link}
                                  href={`sms:${searchedCustomerData.gsm}`}
                                >
                                  <MessageSquareShare size={16} />
                                </Button>
                              </div>
                            )}

                          {searchedCustomerData?.customerNotes.map(
                            (customerNote) => (
                              <div
                                key={customerNote.id}
                                className="border-primary bg-primary/5 rounded-xl border-2 p-2"
                              >
                                <div className="mb-1 flex flex-wrap gap-1">
                                  {customerNote.tags.map((tag) => (
                                    <Chip
                                      key={tag.name}
                                      variant="flat"
                                      size="sm"
                                      color="primary"
                                    >
                                      {tag.name}
                                    </Chip>
                                  ))}
                                </div>
                                <p className="px-1 text-xs leading-4 break-all text-gray-500">
                                  {customerNote.note}
                                </p>
                              </div>
                            ),
                          )}
                        </CardBody>
                        {searchedCustomerData?.formBlocked ||
                        searchedCustomerData?.notes ? (
                          <CardFooter className="pt-0">
                            {searchedCustomerData?.formBlocked ? (
                              <p className="text-danger-500 text-xs">
                                <Trans>
                                  Stranka ima blokirano spletno naročanje
                                </Trans>
                              </p>
                            ) : null}
                            {searchedCustomerData?.notes && (
                              <p className="text-xs leading-4 break-all text-gray-500">
                                <TextWithLinks
                                  text={searchedCustomerData?.notes}
                                />
                              </p>
                            )}
                          </CardFooter>
                        ) : null}
                      </Card>
                    </Skeleton>
                  </div>
                ) : null}

                {isContactCustomerFeatureEnabled ? (
                  <div className="pt-1">
                    {!isContactPersonSectionOpen ? (
                      <Button
                        variant="flat"
                        color="default"
                        startContent={<Plus size={16} />}
                        onPress={() => {
                          setIsSelectingContactCustomer(false);
                          setIsContactPersonSectionOpen(true);
                          setIsContactPersonSectionDismissed(false);
                          setContactPersonSelectionError(null);
                        }}
                      >
                        <Trans>Dodaj kontaktno osebo na termin</Trans>
                      </Button>
                    ) : (
                      <div className="flex flex-1 flex-col gap-3">
                        <div className="flex w-full items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">
                              <Trans>Kontaktna oseba (opcijsko)</Trans>
                            </p>
                            <LimeInfoTooltip
                              ariaLabel="Informacije o kontaktni osebi"
                              content={
                                <p className="max-w-56 text-xs leading-5">
                                  <Trans>
                                    Izberi predlagan kontakt ali dodaj novega.
                                    Če kontaktne osebe ne izbereš, obvestila
                                    prejme stranka.
                                  </Trans>
                                </p>
                              }
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button
                              variant="flat"
                              size="sm"
                              color="default"
                              startContent={<X size={14} />}
                              onPress={cancelContactPersonSelection}
                            >
                              {isNewContactSelected ? (
                                <Trans>Prekliči</Trans>
                              ) : (
                                <Trans>Prekliči dodajanje kontakta</Trans>
                              )}
                            </Button>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3">
                          {contactPersonSelectionError ? (
                            <p className="text-danger-500 text-xs">
                              {contactPersonSelectionError}
                            </p>
                          ) : null}

                          {form.values.contactPersonMode === "existing" &&
                          selectedExistingContact &&
                          !isSelectingContactCustomer ? (
                            <div className="flex flex-col gap-3">
                              <div className="border-default-200 bg-default-50 rounded-xl border px-3 py-3 transition-all duration-200">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium">
                                      {selectedExistingContact.name}{" "}
                                      {selectedExistingContact.lastName}
                                    </p>

                                    <Button
                                      isIconOnly
                                      size="sm"
                                      variant="flat"
                                      onPress={() => {
                                        openContactCustomerCard(
                                          selectedExistingContact.customerId,
                                        );
                                      }}
                                    >
                                      <ArrowUpRight size={16} />
                                    </Button>
                                  </div>
                                  {selectedExistingContact.email ? (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        startContent={<Mail size={16} />}
                                        variant="flat"
                                        size="sm"
                                        className="w-fit"
                                        as={Link}
                                        href={`mailto:${selectedExistingContact.email}`}
                                      >
                                        {selectedExistingContact.email}
                                      </Button>

                                      <CopyButton
                                        size="sm"
                                        variant="secondary"
                                        text={selectedExistingContact.email}
                                      />
                                    </div>
                                  ) : null}
                                  {selectedExistingContact.gsm ? (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        startContent={<Phone size={16} />}
                                        variant="flat"
                                        size="sm"
                                        className="w-fit"
                                        as={Link}
                                        href={`tel:${selectedExistingContact.gsm}`}
                                      >
                                        {selectedExistingContact.gsm}
                                      </Button>

                                      <CopyButton
                                        size="sm"
                                        variant="secondary"
                                        text={selectedExistingContact.gsm}
                                      />

                                      <Button
                                        isIconOnly
                                        size="sm"
                                        variant="flat"
                                        as={Link}
                                        href={`sms:${selectedExistingContact.gsm}`}
                                      >
                                        <MessageSquareShare size={16} />
                                      </Button>
                                    </div>
                                  ) : null}
                                  {selectedExistingContact.relationCount > 0 ? (
                                    <p className="text-default-500 text-xs">
                                      <Trans>Povezano terminov:</Trans>{" "}
                                      {selectedExistingContact.relationCount}
                                    </p>
                                  ) : null}
                                </div>
                              </div>

                              <div className="flex flex-wrap justify-end gap-1">
                                <Button
                                  variant="flat"
                                  size="sm"
                                  color="primary"
                                  startContent={<UserSearch size={16} />}
                                  onPress={() => {
                                    setIsSelectingContactCustomer(true);
                                  }}
                                >
                                  <Trans>Zamenjaj kontakt</Trans>
                                </Button>
                                <Button
                                  variant="flat"
                                  size="sm"
                                  color="danger"
                                  startContent={<X size={16} />}
                                  onPress={clearContactPersonFields}
                                >
                                  <Trans>Odstrani kontakt</Trans>
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {isFetchingContactSuggestions ? (
                                <Spinner size="sm" />
                              ) : null}

                              {!isNewContactSelected ? (
                                <div className="flex flex-col gap-2 pt-1">
                                  {contactSuggestions.length > 0 ? (
                                    <p className="text-default-500 text-xs font-medium">
                                      <Trans>Predlagani kontakti</Trans>
                                    </p>
                                  ) : null}

                                  <div className="flex flex-wrap gap-2">
                                    {contactSuggestions.map((contact) => (
                                      <Button
                                        key={contact.customerId}
                                        variant="flat"
                                        color="default"
                                        className="border-default-200 bg-default-50 hover:border-default-300 h-auto min-h-[76px] min-w-0 grow basis-[calc((100%-16px)/3)] items-start justify-start rounded-xl border px-4 py-3 text-left whitespace-normal transition-all"
                                        onPress={() => {
                                          setIsSelectingContactCustomer(false);
                                          setSelectedAutocompleteContact(null);
                                          form.setFieldValue(
                                            "contactPersonMode",
                                            "existing",
                                          );
                                          form.setFieldValue(
                                            "contactCustomerId",
                                            contact.customerId,
                                          );
                                          form.setFieldValue("contactName", "");
                                          form.setFieldValue(
                                            "contactLastName",
                                            "",
                                          );
                                          form.setFieldValue(
                                            "contactEmail",
                                            "",
                                          );
                                          form.setFieldValue("contactGsm", "");
                                          form.setFieldValue(
                                            "contactCountryCode",
                                            "",
                                          );
                                          form.clearFieldError("contactName");
                                          form.clearFieldError(
                                            "contactLastName",
                                          );
                                          form.clearFieldError("contactEmail");
                                          form.clearFieldError("contactGsm");
                                        }}
                                      >
                                        <div className="flex flex-col items-start text-left">
                                          <span className="text-default-900 text-sm leading-5 font-semibold break-words whitespace-normal">
                                            {contact.name} {contact.lastName}
                                          </span>
                                          {contact.relationCount > 0 ? (
                                            <span className="text-default-500 mt-1 text-xs leading-4">
                                              <Trans>Povezano terminov:</Trans>{" "}
                                              {contact.relationCount}
                                            </span>
                                          ) : null}
                                        </div>
                                      </Button>
                                    ))}

                                    <Button
                                      variant="flat"
                                      color="default"
                                      onPress={() => {
                                        setIsSelectingContactCustomer(false);
                                        form.setFieldValue(
                                          "contactPersonMode",
                                          "new",
                                        );
                                        form.setFieldValue(
                                          "contactCustomerId",
                                          null,
                                        );
                                        form.clearFieldError("contactName");
                                        form.clearFieldError("contactLastName");
                                        form.clearFieldError("contactEmail");
                                        form.clearFieldError("contactGsm");
                                      }}
                                      className={cn(
                                        "h-auto min-h-[92px] min-w-0 grow basis-[calc((100%-16px)/3)] items-start justify-start rounded-xl border px-4 py-3 text-left whitespace-normal transition-all",
                                        isNewContactSelected
                                          ? "border-primary bg-primary/10 shadow-sm"
                                          : "border-default-200 bg-default-50 hover:border-default-300",
                                      )}
                                    >
                                      <div className="flex flex-col items-start text-left">
                                        <span
                                          className={cn(
                                            "text-sm leading-5 font-semibold break-words whitespace-normal",
                                            isNewContactSelected
                                              ? "text-primary-700"
                                              : "text-default-900",
                                          )}
                                        >
                                          <Trans>Dodaj nov kontakt</Trans>
                                        </span>
                                        <span
                                          className={cn(
                                            "mt-1 text-[11px] leading-4 break-words whitespace-normal",
                                            isNewContactSelected
                                              ? "text-primary-600"
                                              : "text-default-500",
                                          )}
                                        >
                                          <Trans>
                                            Vnesi podatke kontaktne osebe ročno.
                                          </Trans>
                                        </span>
                                      </div>
                                    </Button>
                                  </div>
                                </div>
                              ) : null}

                              <LimeCollapse
                                isOpen={form.values.contactPersonMode === "new"}
                              >
                                <div className="flex flex-col gap-2 pt-1">
                                  <div className="flex items-center gap-3">
                                    <p className="text-default-500 text-xs font-medium">
                                      <Trans>Podatki kontaktne osebe</Trans>
                                    </p>
                                  </div>

                                  <div className="flex flex-col gap-2 md:flex-row">
                                    <FormInput
                                      form={form}
                                      isFetchingCustomers={
                                        isFetchingContactCustomers
                                      }
                                      customersData={contactCustomersData}
                                      onCustomerSelected={
                                        selectExistingContactCustomer
                                      }
                                      setCombinedCustomerSearch={
                                        setCombinedContactCustomerSearch
                                      }
                                      setSelectedCustomerIdField={null}
                                      selectInputProps={{
                                        inputType: "text",
                                        inputProps: {
                                          ...form.getInputProps("contactName"),
                                          onChange: (
                                            e: ChangeEvent<HTMLInputElement>,
                                          ) => {
                                            form.setFieldValue(
                                              "contactName",
                                              e.target.value,
                                            );
                                            setCurrentContactSearchField(
                                              "name",
                                            );
                                            handleSearchContactCustomers();
                                          },
                                          label: t`Ime kontaktne osebe`,
                                          "data-identifier": "contact-name",
                                          autoComplete: "do-not-autofill",
                                          size: "sm",
                                        },
                                      }}
                                    />

                                    <FormInput
                                      form={form}
                                      isFetchingCustomers={
                                        isFetchingContactCustomers
                                      }
                                      customersData={contactCustomersData}
                                      onCustomerSelected={
                                        selectExistingContactCustomer
                                      }
                                      setCombinedCustomerSearch={
                                        setCombinedContactCustomerSearch
                                      }
                                      setSelectedCustomerIdField={null}
                                      selectInputProps={{
                                        inputType: "text",
                                        inputProps: {
                                          ...form.getInputProps(
                                            "contactLastName",
                                          ),
                                          onChange: (
                                            e: ChangeEvent<HTMLInputElement>,
                                          ) => {
                                            form.setFieldValue(
                                              "contactLastName",
                                              e.target.value,
                                            );
                                            setCurrentContactSearchField(
                                              "lastName",
                                            );
                                            handleSearchContactCustomers();
                                          },
                                          label: t`Priimek kontaktne osebe`,
                                          "data-identifier":
                                            "contact-last-name",
                                          autoComplete: "do-not-autofill",
                                          size: "sm",
                                        },
                                      }}
                                    />
                                  </div>

                                  <FormInput
                                    form={form}
                                    isFetchingCustomers={
                                      isFetchingContactCustomers
                                    }
                                    customersData={contactCustomersData}
                                    onCustomerSelected={
                                      selectExistingContactCustomer
                                    }
                                    setCombinedCustomerSearch={
                                      setCombinedContactCustomerSearch
                                    }
                                    setSelectedCustomerIdField={null}
                                    selectInputProps={{
                                      inputType: "text",
                                      inputProps: {
                                        ...form.getInputProps("contactEmail"),
                                        onChange: (
                                          e: ChangeEvent<HTMLInputElement>,
                                        ) => {
                                          form.setFieldValue(
                                            "contactEmail",
                                            e.target.value,
                                          );
                                          setCurrentContactSearchField("email");
                                          handleSearchContactCustomers();
                                        },
                                        label: t`E-pošta kontaktne osebe`,
                                        "data-identifier": "contact-email",
                                        autoComplete: "do-not-autofill",
                                        size: "sm",
                                      },
                                    }}
                                  />

                                  <FormInput
                                    form={form}
                                    isFetchingCustomers={
                                      isFetchingContactCustomers
                                    }
                                    customersData={contactCustomersData}
                                    onCustomerSelected={
                                      selectExistingContactCustomer
                                    }
                                    setCombinedCustomerSearch={
                                      setCombinedContactCustomerSearch
                                    }
                                    setSelectedCustomerIdField={null}
                                    selectInputProps={{
                                      inputType: "phone",
                                      inputProps: {
                                        ...form.getInputProps("contactGsm"),
                                        onChange: (
                                          value: Extract<
                                            LimeSelectInputProps,
                                            { inputType: "phone" }
                                          >["inputProps"]["onChange"],
                                        ) => {
                                          form.setFieldValue(
                                            "contactGsm",
                                            value,
                                          );
                                          setCurrentContactSearchField("gsm");
                                          handleSearchContactCustomers();
                                        },
                                        label: t`Telefonska številka kontaktne osebe`,
                                        defaultCountry:
                                          locations?.find(
                                            (l: any) =>
                                              l.locationId ===
                                              selectedLocationId,
                                          )?.country || "SI",
                                        autoComplete: "do-not-autofill",
                                      },
                                    }}
                                  />
                                </div>
                              </LimeCollapse>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <Divider />

            <div className="flex flex-col gap-2 px-1 py-1">
              <LimeTextarea
                label={t`Zapisek termina`}
                {...form.getInputProps("comment")}
                value={form.values.comment ?? ""}
                data-identifier={"appointment-comment"}
                minRows={4}
                isDisabled={!permissions.manage_appointments}
              />

              <CustomerNoteLabelPicker
                selectedTagIds={form.values.selectedTagIds}
                selectedDefaultIds={form.values.selectedDefaultIds}
                onChangeTagIds={(ids) =>
                  form.setFieldValue("selectedTagIds", ids)
                }
                onChangeDefaultIds={(ids) =>
                  form.setFieldValue("selectedDefaultIds", ids)
                }
                isDisabled={!permissions.manage_appointments}
              />

              {Array.isArray(form.values.extraDetails) &&
                form.values.extraDetails.length > 0 && (
                  <div className="flex flex-col">
                    <p className="text-sm leading-5 font-medium">
                      <Trans>Dodatne informacije</Trans>
                    </p>

                    <table className="w-full border-collapse rounded-xl bg-gray-100">
                      {form.values.extraDetails.map(
                        (
                          extraDetail: FormValues["extraDetails"][number],
                          index,
                        ) => {
                          const inputElement = (() => {
                            switch (extraDetail.type) {
                              case "multi-select":
                              case "checkbox":
                                return (
                                  <p>
                                    {extraDetail.value
                                      .map(
                                        (v: any) =>
                                          v.defaultLanguage ||
                                          Object.values(v)[0],
                                      )
                                      .join(", ")}
                                  </p>
                                );
                              case "radio":
                              case "select":
                                return (
                                  <p>
                                    {extraDetail.value.defaultLanguage ||
                                      Object.values(extraDetail.value)[0]}
                                  </p>
                                );
                              case "date":
                                return (
                                  <p>
                                    {dayjs(extraDetail.value, [
                                      "MM-DD-YYYYTHH:mm",
                                      "YYYY-MM-DD",
                                    ])
                                      .toDate()
                                      .toLocaleString(locale, {
                                        day: "2-digit",
                                        month: "numeric",
                                        year: "numeric",
                                      })}
                                  </p>
                                );
                              case "time":
                                return (
                                  <p>
                                    {dayjs(extraDetail.value, [
                                      "HH:mm",
                                      "MM-DD-YYYYTHH:mm",
                                    ])
                                      .toDate()
                                      .toLocaleString(locale, {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                  </p>
                                );
                              default:
                                return <p>{extraDetail.value}</p>;
                            }
                          })();

                          return (
                            <tr
                              key={extraDetail.id ?? extraDetail.field ?? index}
                            >
                              <td className="py-1 pl-2">
                                <p className="text-sm leading-5 font-medium">
                                  {getLocalizedName(
                                    Object.entries<any>(extraDetail.label).map(
                                      ([lang, value]) => ({
                                        language: lang,
                                        name: value,
                                      }),
                                    ),
                                  )}
                                </p>
                              </td>
                              <td className="py-0">{inputElement}</td>
                            </tr>
                          );
                        },
                      )}
                    </table>
                  </div>
                )}
            </div>

            {isFeatureEnabled("subscriptions") &&
            form.getValues().customerId != null ? (
              <CustomerSubscriptionsList
                customerId={form.getValues().customerId!}
                customerSubscriptionId={form.getValues().customerSubscriptionId}
                onCustomerSubscriptionChange={(customerSubscriptionId) => {
                  form.setFieldValue(
                    "customerSubscriptionId",
                    customerSubscriptionId,
                  );
                  if (customerSubscriptionId != null) {
                    const zeroedServices = selectedServices.map((service) => ({
                      ...service,
                      price: 0,
                    }));
                    setSelectedServices(zeroedServices, false, false);
                  } else {
                    setSelectedServices(selectedServices, true, false);
                  }
                }}
                isNewAppointment={isNewAppointment}
                locationId={notificationLocationId}
                allowInitialAutoSelect={
                  isNewAppointment ||
                  (appointmentData?.isPreScheduled && !customerData)
                }
              />
            ) : null}

            {areNotificationSettingsLoading ? (
              <Spinner className="mt-4" />
            ) : null}

            {form.getValues().additionalPrescheduledAppointmentIds.length >
            0 ? (
              <Divider />
            ) : undefined}

            {!isExistingUserAppointmentSelected && !isNewAppointment ? (
              <Button
                onPress={onOpenPrescheduledSelectModal}
                variant="flat"
                color="primary"
                startContent={<SquareStack size={16} />}
              >
                <Trans>Dodaj stranko na več skupinskih terminov</Trans>
              </Button>
            ) : undefined}

            {form.getValues().additionalPrescheduledAppointmentIds.length >
            0 ? (
              <>
                <p className="text-sm leading-5 font-medium">
                  <Trans>
                    Št. terminov na katere bo stranka dodana:{" "}
                    {
                      form.getValues().additionalPrescheduledAppointmentIds
                        .length
                    }
                  </Trans>
                </p>
                <Divider />
              </>
            ) : null}

            {isNewAppointment ||
            (appointmentData?.isPreScheduled && !customerData) ? (
              notificationSettingToggles.length ? (
                <div className="flex flex-col gap-1 border-b-1 border-gray-200 pb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      <Trans>Obvestila</Trans>
                    </span>
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      onPress={() => {
                        const areAllEnabled = notificationSettingToggles.every(
                          (setting) => setting.enabled,
                        );

                        setNotificationSettingToggles(
                          notificationSettingToggles.map((setting) => ({
                            ...setting,
                            enabled: !areAllEnabled,
                          })),
                        );
                      }}
                    >
                      {notificationSettingToggles.every(
                        (setting) => setting.enabled,
                      ) ? (
                        <Trans>Izključi vse</Trans>
                      ) : (
                        <Trans>Vključi vse</Trans>
                      )}
                    </Button>
                  </div>

                  <AppointmentNotificationsSection
                    isNewAppointment
                    settings={notificationSettings?.settings}
                    notificationLog={[]}
                    notificationSchedule={[]}
                    disabledNotificationSettingIds={[]}
                    toggles={notificationSettingToggles}
                    onToggle={setNotificationSettingEnabled}
                    selectedServices={selectedServices}
                    locationId={notificationLocationId}
                    notificationChannels={notificationChannels}
                  />
                </div>
              ) : null
            ) : (
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  <Trans>Obvestila</Trans>
                </span>
                <div className="pb-2">
                  <AppointmentNotificationsSection
                    settings={notificationSettings?.settings}
                    notificationLog={notificationLog}
                    notificationSchedule={notificationSchedule}
                    disabledNotificationSettingIds={
                      disabledNotificationSettingIds
                    }
                    batchConfirmation={customerData?.batchConfirmation ?? null}
                    toggles={notificationSettingToggles}
                    onToggle={setNotificationSettingEnabled}
                    selectedServices={selectedServices}
                    locationId={notificationLocationId}
                    notificationChannels={notificationChannels}
                  />
                </div>
                {isFeatureEnabled("messageSchedule") &&
                !isNewAppointment &&
                customerData?.userAppointmentId ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <p className="text-sm leading-5 font-medium">
                        <Trans>Sporočila</Trans>
                      </p>
                      <MessageModal
                        userAppointmentId={customerData.userAppointmentId}
                      ></MessageModal>
                    </div>

                    <div className="flex flex-col">
                      <UserAppointmentMessageList
                        userAppointmentId={customerData.userAppointmentId}
                      ></UserAppointmentMessageList>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            {isNewAppointment &&
              isPaymentsFeatureEnabled &&
              canCreateOnlinePayment && (
                <div
                  className={cn("rounded-lg transition-all", {
                    "bg-stone-100 p-2": form.values.createPayment,
                  })}
                >
                  <LimeSwitch
                    title={t`Zahtevaj plačilo in ga pošlji stranki`}
                    {...form.getInputProps("createPayment", {
                      type: "checkbox",
                    })}
                    description={t`Potrebna je tel. št. ali e-mail`}
                    isCompact
                    className="mb-2"
                  />

                  <LimeCollapse isOpen={form.values.createPayment}>
                    <div>
                      <div className="flex items-center gap-2 pb-2">
                        {!isPaymentPricePending &&
                        calculatedPaymentPriceData?.success != false ? (
                          <>
                            {isCustomOnlinePaymentAmount ? (
                              <>
                                <LimeNumberInput
                                  label={t`Za plačati`}
                                  variant="bordered"
                                  size="sm"
                                  {...form.getInputProps(
                                    "overrideCreatePaymentAmount",
                                  )}
                                  minValue={0}
                                  step={0.01}
                                />

                                <Button
                                  variant="flat"
                                  size="lg"
                                  key="dismiss-custom-online-payment-amount"
                                  isIconOnly
                                  onPress={() => {
                                    setIsCustomOnlinePaymentAmount(false);
                                    form.setFieldValue(
                                      "overrideCreatePaymentAmount",
                                      null,
                                    );
                                  }}
                                >
                                  <X size={16} />
                                </Button>
                              </>
                            ) : (
                              <>
                                <p
                                  className={cn(
                                    "py-2 pl-2 text-sm font-medium transition-colors",
                                    {
                                      "text-gray-500": isPaymentPricePending,
                                    },
                                  )}
                                >
                                  <Trans>Za plačati</Trans>{" "}
                                  {!paymentPriceErrorMessage &&
                                    calculatedPaymentPriceData?.formattedPrice}
                                </p>

                                <Button
                                  isIconOnly
                                  variant="light"
                                  size="sm"
                                  key="edit-online-payment-amount"
                                  onPress={() =>
                                    setIsCustomOnlinePaymentAmount(true)
                                  }
                                >
                                  <Pencil size={16} />
                                </Button>
                              </>
                            )}
                          </>
                        ) : (
                          <p>
                            <Trans>Napaka pri izračunu cene</Trans>
                          </p>
                        )}
                        {isPaymentPricePending && <Spinner size="sm" />}
                      </div>
                      <LimeAlert
                        className="flex-1"
                        message={paymentPriceErrorMessage}
                        color="danger"
                      />

                      <div className="mb-2">
                        <LimeNumberInput
                          label={t`Čas za plačilo (min)`}
                          minValue={1}
                          size="sm"
                          variant="bordered"
                          step={1}
                          {...form.getInputProps("createPaymentTimeoutMinutes")}
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <LimeLocalePicker
                          {...form.getInputProps("createPaymentLocale")}
                          className="flex-1"
                          variant="bordered"
                          size="sm"
                          label={undefined}
                        />

                        <LimeSelect
                          {...form.getInputProps("createPaymentChannel")}
                          items={[
                            {
                              key: "sms",
                              label: t`SMS`,
                            },
                            {
                              key: "email",
                              label: t`Email`,
                            },
                            {
                              key: "both",
                              label: t`SMS + Email`,
                            },
                          ].filter(
                            (item) =>
                              isSmsFeatureEnabled || item.key === "email",
                          )}
                          disallowEmptySelection
                          className="flex-1"
                          variant="bordered"
                          size="sm"
                          selectionMode="single"
                        />
                      </div>
                    </div>
                  </LimeCollapse>
                </div>
              )}
            {isNewAppointment && (
              <div
                className={cn("rounded-md transition-all", {
                  "bg-stone-100 p-2": form.values.isRepeating,
                })}
              >
                <LimeSwitch
                  data-identifier={"repeating-appointment-switch"}
                  title={t`Ponavljajoč termin`}
                  {...form.getInputProps("isRepeating", {
                    type: "checkbox",
                  })}
                  isCompact
                />
                {form.values.isRepeating && (
                  <div className="mt-3 flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm whitespace-pre">
                        <Trans>Ponovi vsak</Trans>
                      </p>
                      <LimeNumberInput
                        data-identifier={"repeat-interval"}
                        placeholder={"1"}
                        {...form.getInputProps("repeatInterval")}
                        minValue={1}
                        size="sm"
                        variant="bordered"
                        step={1}
                      />
                      <LimeSelect
                        data-identifier={"repeat-type-select"}
                        {...form.getInputProps("repeatType")}
                        variant="bordered"
                        items={[
                          { label: t`Dan`, key: "day" },
                          { label: t`Teden`, key: "week" },
                          { label: t`Mesec`, key: "month" },
                        ]}
                        size="lg"
                        defaultValue={"week"}
                      />
                    </div>
                    <Tabs
                      aria-label={t`Način zaključka ponavljanja`}
                      size="sm"
                      fullWidth
                      selectedKey={form.values.repeatEndType ?? "date"}
                      onSelectionChange={(key) => {
                        form.setFieldValue(
                          "repeatEndType",
                          key as "date" | "count",
                        );
                        if (key === "date" && !form.values.repeatUntilDate) {
                          const steps = Math.max(
                            1,
                            (form.values.repeatDuration || 1) - 1,
                          );
                          form.setFieldValue(
                            "repeatUntilDate",
                            dayjs(dayObject)
                              .add(
                                steps * (form.values.repeatInterval || 1),
                                form.values.repeatType,
                              )
                              .format("YYYY-MM-DD"),
                          );
                        }
                      }}
                    >
                      <Tab
                        key="date"
                        data-identifier={"repeat-end-date-tab"}
                        title={t`Ponavljaj do datuma`}
                      >
                        <div className="flex flex-col gap-2">
                          <LimeDatePicker
                            data-identifier={"repeat-until-date"}
                            label={t`Ponavljaj do`}
                            size="sm"
                            granularity="day"
                            value={
                              form.values.repeatUntilDate
                                ? parseDate(form.values.repeatUntilDate)
                                : null
                            }
                            minValue={parseDate(
                              dayjs(dayObject).format("YYYY-MM-DD"),
                            )}
                            onChange={(value) => {
                              form.setFieldValue(
                                "repeatUntilDate",
                                value ? value.toString() : null,
                              );
                            }}
                          />
                          {form.values.repeatUntilDate &&
                            (() => {
                              const count = computeRepeatCountFromUntil({
                                startDate: dayObject,
                                untilDate: form.values.repeatUntilDate,
                                interval: form.values.repeatInterval,
                                type: form.values.repeatType,
                              });

                              const lastDate = dayjs(dayObject)
                                .add(
                                  (count - 1) *
                                    (form.values.repeatInterval || 1),
                                  form.values.repeatType,
                                )
                                .toDate()
                                .toLocaleDateString(i18n.locale);

                              return (
                                <p className="text-tiny text-default-500">
                                  <Trans>
                                    Ustvari {count} terminov, zadnji {lastDate}
                                  </Trans>
                                </p>
                              );
                            })()}
                        </div>
                      </Tab>
                      <Tab
                        key="count"
                        data-identifier={"repeat-end-count-tab"}
                        title={t`Število ponavljanj`}
                      >
                        <LimeNumberInput
                          data-identifier={"repeat-duration"}
                          label={t`Število ponavljanj`}
                          placeholder={"1"}
                          {...form.getInputProps("repeatDuration")}
                          minValue={1}
                          size="sm"
                          variant="bordered"
                          step={1}
                        />
                      </Tab>
                    </Tabs>
                    {canCreateOnlinePayment && form.values.createPayment && (
                      <LimeSwitch
                        title={t`Plačaj samo prvi termin`}
                        {...form.getInputProps("payOnlyFirstAppointment", {
                          type: "checkbox",
                        })}
                        isCompact
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            {customerData?.customFields != null &&
              customerData?.customFields?.length > 0 && (
                <div className="rounded-lg bg-[#F7F7F7] px-2 py-1">
                  <p className="text-sm leading-5 font-medium">
                    <Trans>Polja po meri</Trans>
                  </p>
                  <div className="flex flex-col gap-1">
                    {customerData?.customFields.map(
                      (field: any, index: number) => {
                        let value = field.value;
                        if (dayjs(field.value, "YYYY-MM-DD").isValid()) {
                          value = dayjs(field.value, "YYYY-MM-DD").format(
                            "DD.MM.YYYY",
                          );
                        }

                        return (
                          <div className="flex gap-1" key={field.field}>
                            <p className="text-sm leading-5 font-medium">
                              {field.field}:{" "}
                            </p>
                            <p className="text-sm">{value}</p>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
            <button
              id={"appointment-form-submit"}
              type={"submit"}
              className="hidden"
            ></button>
          </form>

          {isFeatureEnabled("customerFiles") &&
            canViewCustomerNotesFiles &&
            customerData?.userAppointmentId && (
              <AppointmentCustomerFiles
                customerId={customerData?.customerId}
                userAppointmentId={customerData.userAppointmentId}
              />
            )}
        </div>

        <CustomerModal
          isOpen={isCustomerModalOpen}
          preSelectedCustomerId={form.getValues().customerId ?? undefined}
          types={["physical"]}
          confirmButtonProps={{
            text: t`Zamenjaj stranko`,
          }}
          handleClose={(data?: { customerId?: number; companyId?: number }) => {
            onCloseCustomerModal();

            if (data?.customerId != null) {
              form.setFieldValue("comment", null);
              form.setFieldValue("customerId", data.customerId);
              form.setFieldValue("customerSubscriptionId", null);
              clearContactPersonFields();
            }
          }}
        />

        <ManageCustomerModal
          isOpen={isManageCustomerModalOpen}
          handleClose={({ customerId, companyId }) => {
            onCloseManageCustomerModal();

            if (customerId != null || companyId != null) {
              fetchAppointmentData(appointmentData?.appointmentId);
            }
          }}
          editingEntityId={form.getValues().customerId ?? undefined}
          defaultEntityType="physical"
        />
      </div>

      {isPreScheduled ? (
        <PrescheduledSelectModal
          excludeAppointmentIds={
            appointmentData?.appointmentId
              ? [appointmentData?.appointmentId]
              : undefined
          }
          overrideLocationIds={[selectedLocationId]}
          attendeeCustomerId={form.getValues().customerId}
          overrideServiceIds={selectedServices?.map(
            (service) => service.serviceId,
          )}
          isOpen={isPrescheduledSelectModalOpen}
          customerSubscriptionId={form.getValues().customerSubscriptionId}
          appointmentDate={
            appointmentData
              ? dayjs(appointmentData?.startTime).format("YYYY-MM-DD")
              : undefined
          }
          fromAppointmentId={appointmentData?.appointmentId}
          onClose={(selectedIds?: number[]) => {
            onClosePrescheduledSelectModal();

            if (selectedIds) {
              form.setFieldValue(
                "additionalPrescheduledAppointmentIds",
                selectedIds,
              );
            }
          }}
        />
      ) : undefined}

      <Modal
        isOpen={isCreateAppointmentWithoutCustomerModalOpen}
        onClose={onCloseCreateAppointmentWithoutCustomerModal}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                <Trans>Želite ustvariti termin brez stranke?</Trans>
              </ModalHeader>
              <ModalBody>
                <Trans>
                  Generirana bo nova stranka z imenom &quot;Brez Imena&quot; in
                  naključnim priimkom. Želite nadaljevati?
                </Trans>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="flat"
                  onPress={() => {
                    setCanSubmitForm(true);
                    setCanDismissDrawer(true);
                    onClose();
                  }}
                >
                  <Trans>Prekliči</Trans>
                </Button>
                <Button
                  color={"primary"}
                  onPress={() => {
                    setCanSubmitForm(true);

                    const validation = form.validate();
                    if (validation.hasErrors) {
                      return;
                    }

                    submitAppointment(form.values);

                    onClose();
                    setCanDismissDrawer(true);
                  }}
                >
                  <Trans>Potrdi</Trans>
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

const FormInput = ({
  form,
  isFetchingCustomers,
  customersData,
  setCombinedCustomerSearch,
  selectInputProps,
  selectFirstOnEnter = false,
  onCustomerSelected,
  setSelectedCustomerIdField = "customerId",
}: {
  form: UseFormReturnType<any>;
  isFetchingCustomers: boolean;
  customersData: GetCustomers["response"] | undefined;
  setCombinedCustomerSearch: (search: string) => void;
  selectFirstOnEnter?: boolean;
  onCustomerSelected?: (
    customer: GetCustomers["response"]["customers"][number],
  ) => void;
  setSelectedCustomerIdField?: "customerId" | null;
} & (
  | {
      selectInputProps: Omit<
        LimeSelectTextInputProps,
        "items" | "onSelect" | "selectFirstOnEnter"
      >;
    }
  | {
      selectInputProps: Omit<
        LimeSelectPhoneInputProps,
        "items" | "onSelect" | "selectFirstOnEnter"
      >;
    }
)) => {
  const items =
    customersData?.customers.map((customer) => ({
      key: customer.customerId.toString(),
      label: (
        <div className="flex-1">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            {customer.name} {customer.lastName}
            {customer.isGlobalCustomer ? (
              <GlobalCustomerIndicator size={14} />
            ) : null}
          </p>
          {customer.email && customer.emailVisibility !== "hidden" && (
            <p className="text-xs">{customer.email}</p>
          )}
          {customer.gsm && customer.gsmVisibility !== "hidden" && (
            <p className="text-xs">{customer.gsm}</p>
          )}
          {customer.appointmentNoteTags &&
          customer.appointmentNoteTags.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {customer.appointmentNoteTags.map((tagName) => (
                <Chip key={tagName} variant="flat" size="sm" color="primary">
                  {tagName}
                </Chip>
              ))}
            </div>
          ) : null}
          {customer.contactCustomerCount > 0 ? (
            <p className="text-default-500 text-xs">
              <Trans>Kontaktnih oseb:</Trans> {customer.contactCustomerCount}
            </p>
          ) : null}
          {customer.formBlocked ? (
            <p className="text-danger-500 text-xs">
              <Trans>Stranka ima blokirano spletno naročanje</Trans>
            </p>
          ) : null}
          {customer.notes && (
            <div
              style={{
                flex: 1,
              }}
            >
              <p className="text-xs leading-4 text-gray-500">
                {customer.notes.length > NOTES_TRUNCATE_LENGTH
                  ? `${customer.notes.substring(0, NOTES_TRUNCATE_LENGTH)}...`
                  : customer.notes}
              </p>
            </div>
          )}
        </div>
      ),
    })) ?? [];

  if (selectInputProps.inputType === "text") {
    return (
      <LimeSelectInput
        inputType={"text"}
        inputProps={{
          ...selectInputProps.inputProps,
          isLoading: isFetchingCustomers,
          className: "flex-1",
          size: "sm" as const,
          autoComplete: "do-not-autofill",
          endContent: isFetchingCustomers ? <Spinner size={"sm"} /> : null,
        }}
        wrapperClassName={selectInputProps.wrapperClassName}
        items={items}
        onSelect={(value) => {
          const selectedCustomerId = parseInt(value);
          const selectedCustomer = customersData?.customers.find(
            (customer) => customer.customerId === selectedCustomerId,
          );

          if (setSelectedCustomerIdField) {
            form.setFieldValue(setSelectedCustomerIdField, selectedCustomerId);
            if (setSelectedCustomerIdField === "customerId") {
              form.setFieldValue("customerSubscriptionId", null);
            }
          }
          if (selectedCustomer) {
            onCustomerSelected?.(selectedCustomer);
          }
          setCombinedCustomerSearch("");
        }}
        selectFirstOnEnter={selectFirstOnEnter}
      />
    );
  }

  return (
    <LimeSelectInput
      inputType={"phone"}
      inputProps={{
        ...selectInputProps.inputProps,
        isLoading: isFetchingCustomers,
        className: "flex-1",
        autoComplete: "do-not-autofill",
      }}
      wrapperClassName={selectInputProps.wrapperClassName}
      items={items}
      onSelect={(value) => {
        const selectedCustomerId = parseInt(value);
        const selectedCustomer = customersData?.customers.find(
          (customer) => customer.customerId === selectedCustomerId,
        );

        if (setSelectedCustomerIdField) {
          form.setFieldValue(setSelectedCustomerIdField, selectedCustomerId);
          if (setSelectedCustomerIdField === "customerId") {
            form.setFieldValue("customerSubscriptionId", null);
          }
        }
        if (selectedCustomer) {
          onCustomerSelected?.(selectedCustomer);
        }
        setCombinedCustomerSearch("");
      }}
      selectFirstOnEnter={selectFirstOnEnter}
    />
  );
};

export default memo(Form);
